// @flow
import * as React from 'react'
import moment from 'moment'
import cloneDatetime from 'mz-utils/cloneDatetime'
import {
  isLocaleReady,
  whenLocaleReady,
  getActiveLocaleDescriptor,
} from '../core/formatMessage'

// Types
export type Props = {
  value: ?moment | ?string,
  format: string,
  tagName: string | React.ComponentType<any>,
  children?: any,
}

// Component used to wrap a message when it is not yet loaded
// to be able to render the real conent and reduce interface
// changes when actual translations will be loaded
export const TransparentMessage = ({ children }: { children: React.Node }) => (
  <span style={{ color: 'transparent' }}>{children}</span>
)

/**
 * This component help to keep the moments that you are displaying to the
 * user to be in a currently active locale. According to the docs, all moment
 * instances remain their locales unchanged when global `moment.locale()` function
 * is called. This component reflects locale changes, wait when a propper
 * locale will be activated and format the moment in the right locale
 * @extends React
 */
class FormattedMoment extends React.Component<Props> {
  unmounted = false

  static defaultProps = {
    tagName: 'span',
    format: '',
  }

  static contextTypes = {
    mzIntl: () => null,
  }

  componentDidMount() {
    this.loadLanguage()
  }

  componentWillUnmount() {
    this.unmounted = true
  }

  loadLanguage() {
    if (!isLocaleReady(this.context)) {
      whenLocaleReady(this.handleLanguageLoaded, this.context)
      return true
    }
    return false
  }

  handleLanguageLoaded = () => {
    if (!this.loadLanguage() && !this.unmounted) {
      this.forceUpdate()
    }
  }

  renderMessage(): React.Node {
    const { value, tagName: Component, children, format } = this.props
    const activeMomentLocale =
      getActiveLocaleDescriptor(this.context).moment || 'en'
    const formattedStr = value
      ? cloneDatetime(value).locale(activeMomentLocale).format(format)
      : ''

    // Use render children function (if provided) or react createElement.
    // It uses `createElement` directly to avoid `key` warnings.
    return typeof children === 'function'
      ? children(formattedStr)
      : React.createElement(Component, null, formattedStr)
  }

  render() {
    const { children, tagName: Component } = this.props

    if (!isLocaleReady(this.context)) {
      return typeof children !== 'function' ? (
        <Component>
          <TransparentMessage>{this.renderMessage()}</TransparentMessage>
        </Component>
      ) : (
        children('')
      )
    }

    return this.renderMessage()
  }
}

export default FormattedMoment
